/**
 * @flow
 */

import React, { useEffect, useRef, useState } from 'react';
import gql from 'graphql-tag';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import { Icon, Form, Input, Message } from 'semantic-ui-react';
import { injectIntl, FormattedMessage, defineMessages } from 'react-intl';
import { Elements, injectStripe } from 'react-stripe-elements';

import { Modal, Button } from '../../../components';
import client from '../../../apollo-client';

import type { ModalDialog } from '../../../type';
import FormLabel from '../../../components/FormLabel';
import Checkbox from '../../../components/Checkbox';

const intlMessages = defineMessages({
    defaultErrorMessage: {
        id: 'errors.defaultMessage',
        defaultMessage: 'Something went wrong',
    },
});

export const GET_STRIPE_INTENT_MUTATION = gql`
    mutation createStripeInput($input: StripeIntentInput!) {
        createStripeIntent(input: $input) {
            clientSecret
        }
    }
`;

export const UPDATE_STRIPE_SUBSCRIPTION_MUTATION = gql`
    mutation updateStripeSubscription($input: StripeSubscriptionInput!) {
        updateStripeSubscription(input: $input) {
            success
        }
    }
`;

type SalonSubscriptionFormProps = {
    stripe: any,
    secret: any,
    onSubmit: any,
    needInvoice: boolean,
    control: any,
};

/**
 * Stripe card collection lives in a class component because it manages the
 * imperative Stripe Elements lifecycle. The invoice fields are wired to the
 * parent's react-hook-form via the `control` prop and <Controller>.
 */
class _SalonSubscriptionForm extends React.Component {
    state = {
        error: null,
    };

    props: SalonSubscriptionFormProps;

    componentDidMount() {
        this.elements = this.props.stripe.elements({
            mode: 'setup',
            currency: 'eur',
        });
        this.cardElement = this.elements.create('payment');
        this.cardElement.mount(this.refs.card);
    }

    handleSubmit = async ev => {
        try {
            if (ev) {
                ev.preventDefault();
            }

            const { error: submitError } = await this.elements.submit();

            if (submitError) {
                this.setState({ error: submitError.message });
                return;
            }

            const { setupIntent, error } = await this.props.stripe.confirmSetup({
                elements: this.elements,
                clientSecret: this.props.secret,
                confirmParams: {
                    return_url: window.location.href,
                },
                redirect: 'if_required',
            });

            if (error) {
                this.setState({ error: error.message });
                return;
            }

            this.props.onSubmit({ paymentMethod: setupIntent.payment_method });
        } catch (e) {
            this.setState({ error: e.message });
        }
    };

    render() {
        const { needInvoice, control } = this.props;
        const { error } = this.state;
        return (
            <Form onSubmit={this.handleSubmit} error={!!error}>
                {error && (
                    <Message error>
                        <Icon name="warning sign" />
                        {error}
                    </Message>
                )}
                <Form.Group>
                    <Form.Radio
                        label={
                            <FormLabel>
                                <FormattedMessage
                                    id="pages.salon.SalonSusbscriptionDialog.price"
                                    defaultMessage="Monthly subscription {price} / month"
                                    values={{ price: '€24.20' }}
                                />
                            </FormLabel>
                        }
                        value="1"
                        checked
                    />
                </Form.Group>
                <div className="ui hidden divider" />
                <div ref="card" />
                <div className="ui hidden divider" />
                <Form.Field>
                    <Form.Group>
                        <Controller
                            name="needInvoice"
                            control={control}
                            render={({ field: { value, onChange } }) => (
                                <Checkbox
                                    value={value}
                                    onChange={onChange}
                                    label={
                                        <FormLabel>
                                            <FormattedMessage
                                                id="pages.salon.SalonSusbscriptionDialog.needInvoice"
                                                defaultMessage="I need a company invoice"
                                            />
                                        </FormLabel>
                                    }
                                />
                            )}
                        />
                    </Form.Group>
                </Form.Field>
                {needInvoice ? (
                    <div>
                        <Form.Field>
                            <Form.Group widths="equal">
                                <Form.Field>
                                    <FormLabel>
                                        <FormattedMessage
                                            id="pages.salon.SalonSusbscriptionDialog.companyName"
                                            defaultMessage="Company name"
                                        />
                                    </FormLabel>
                                    <Controller
                                        name="companyName"
                                        control={control}
                                        render={({ field: { ref, ...field } }) => (
                                            <Input
                                                {...field}
                                                onChange={(e, data) =>
                                                    field.onChange(data?.value ?? e)
                                                }
                                            />
                                        )}
                                    />
                                </Form.Field>
                                <Form.Field>
                                    <FormLabel>
                                        <FormattedMessage
                                            id="pages.salon.SalonSusbscriptionDialog.companyReg"
                                            defaultMessage="Reg. number"
                                        />
                                    </FormLabel>
                                    <Controller
                                        name="companyReg"
                                        control={control}
                                        render={({ field: { ref, ...field } }) => (
                                            <Input
                                                {...field}
                                                onChange={(e, data) =>
                                                    field.onChange(data?.value ?? e)
                                                }
                                            />
                                        )}
                                    />
                                </Form.Field>
                            </Form.Group>
                        </Form.Field>
                        <Form.Field>
                            <FormLabel>
                                <FormattedMessage
                                    id="pages.salon.SalonSusbscriptionDialog.companyAddress"
                                    defaultMessage="Company address"
                                />
                            </FormLabel>
                            <Controller
                                name="companyAddress"
                                control={control}
                                render={({ field: { ref, ...field } }) => (
                                    <Input
                                        {...field}
                                        onChange={(e, data) =>
                                            field.onChange(data?.value ?? e)
                                        }
                                    />
                                )}
                            />
                        </Form.Field>
                    </div>
                ) : null}
            </Form>
        );
    }
}

export const SalonSubscriptionForm = injectStripe(_SalonSubscriptionForm, {
    withRef: true,
});

type SalonSubscriptionDialogProps = { intl: Object } & ModalDialog;

const SalonSubscriptionDialog = ({
    trigger,
    open,
    onClose,
    intl,
}: SalonSubscriptionDialogProps) => {
    const salonId = useSelector(({ user }) => user.get('salon'));

    const { control, getValues } = useForm({
        defaultValues: {
            needInvoice: false,
            companyName: '',
            companyReg: '',
            companyAddress: '',
        },
    });

    const needInvoice = useWatch({ control, name: 'needInvoice' });

    const [secret, setSecret] = useState(null);
    const [loading, setLoading] = useState(false);
    const stripeFormRef = useRef(null);

    const [createStripeIntent] = useMutation(GET_STRIPE_INTENT_MUTATION);
    const [updateStripeSubscription] = useMutation(
        UPDATE_STRIPE_SUBSCRIPTION_MUTATION,
    );

    // Fetch a fresh Stripe setup intent each time the dialog opens.
    useEffect(() => {
        if (!open) return;
        createStripeIntent({ variables: { input: { salon: salonId } } }).then(
            ({ data }) => setSecret(data.createStripeIntent.clientSecret),
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const onStripeSuccess = async ({ paymentMethod }) => {
        const {
            needInvoice: invoice,
            companyName,
            companyReg,
            companyAddress,
        } = getValues();
        try {
            const { data } = await updateStripeSubscription({
                variables: {
                    input: {
                        salon: salonId,
                        paymentMethod,
                        needInvoice: invoice,
                        companyName,
                        companyReg,
                        companyAddress,
                    },
                },
            });
            if (data.updateStripeSubscription.success) {
                onClose();
                client.reFetchObservableQueries(false);
            }
        } catch (e) {
            alert(intl.formatMessage(intlMessages.defaultErrorMessage));
        }
    };

    const handleActivate = async () => {
        setLoading(true);
        try {
            const instance =
                stripeFormRef.current &&
                stripeFormRef.current.getWrappedInstance
                    ? stripeFormRef.current.getWrappedInstance()
                    : stripeFormRef.current;
            if (instance) {
                await instance.handleSubmit();
            }
        } catch (e) {
            alert('Unknown error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal trigger={trigger} open={open} onClose={onClose} size="small">
            <Modal.Header>
                <FormattedMessage
                    id="pages.salon.SalonSusbscriptionDialog.subscription"
                    defaultMessage="Subscription"
                />
            </Modal.Header>
            <Modal.Content>
                <Elements mode="setup" currency="eur">
                    <SalonSubscriptionForm
                        ref={stripeFormRef}
                        control={control}
                        needInvoice={needInvoice}
                        secret={secret}
                        onSubmit={onStripeSuccess}
                    />
                </Elements>
            </Modal.Content>
            <Modal.Actions>
                <Button
                    secondary
                    onClick={handleActivate}
                    loading={loading}
                    disabled={loading}
                >
                    <FormattedMessage
                        id="pages.salon.SalonSusbscriptionDialog.pay"
                        defaultMessage="Activate subscriptions"
                    />
                </Button>
                <Button onClick={onClose}>
                    <Icon name="remove" />
                    <FormattedMessage
                        id="pages.salon.SalonSusbscriptionDialog.cancel"
                        defaultMessage="Cancel"
                    />
                </Button>
            </Modal.Actions>
        </Modal>
    );
};

export default injectIntl(SalonSubscriptionDialog);
